/**
 * Azure Resource Hooks
 *
 * React Query hooks for fetching Azure resources for connection configuration.
 * Follows LogicAppsUX patterns with 24-hour caching and manual refresh.
 */

import { useQuery, useQueries, type UseQueryResult } from '@tanstack/react-query';
import { useAuth } from '@/auth/useAuth';
import {
  getSubscriptions,
  getTenants,
  getCognitiveAccounts,
  getCognitiveProjects,
  getOpenAIAccounts,
  getAccountDetails,
  getAccountKeys,
  getDeployments,
  getApimInstances,
  getApimApis,
  listResourceGroups,
  type AzureSubscription,
  type AzureTenant,
  type CognitiveAccount,
  type CognitiveProject,
  type AccountDetails,
  type AccountKeys,
  type ModelDeployment,
  type ApimInstance,
  type ApimApi,
  type AzureResourceGroup,
} from '@/services/azureResourceService';
import { fetchOttoProjectArmRefs, type OttoProjectArmRef } from '@/services/resourceGraph';
import type { AutomationProject } from '@/types/automation';
import { apiVersions, armEndpoint, getLogicEndpoint, getRegionalEndpoint } from '@/services/endpoints';
import { isLocalMode } from '@/services/localRuntimeClient';
import { useAppStore } from '@/stores/appStore';
import { getProjectLocation } from '@/utils/system';
import { useMemo } from 'react';

// =============================================================================
// QUERY KEYS
// =============================================================================

export const azureResourceKeys = {
  all: ['azureResources'] as const,
  tenants: () => [...azureResourceKeys.all, 'tenants'] as const,
  subscriptions: () => [...azureResourceKeys.all, 'subscriptions'] as const,
  locations: (subscriptionId: string) =>
    [...azureResourceKeys.all, 'locations', subscriptionId] as const,
  projects: (tenantId?: string | null) =>
    [...azureResourceKeys.all, 'projects', tenantId ?? 'default'] as const,
  /** Prefix key for matching all project queries regardless of tenant */
  projectsPrefix: () => [...azureResourceKeys.all, 'projects'] as const,
  cognitiveAccounts: (subscriptionId: string) =>
    [...azureResourceKeys.all, 'cognitiveAccounts', subscriptionId] as const,
  cognitiveProjects: (subscriptionId: string) =>
    [...azureResourceKeys.all, 'cognitiveProjects', subscriptionId] as const,
  openAIAccounts: (subscriptionId: string) =>
    [...azureResourceKeys.all, 'openAIAccounts', subscriptionId] as const,
  accountDetails: (accountId: string) =>
    [...azureResourceKeys.all, 'accountDetails', accountId] as const,
  accountKeys: (accountId: string) =>
    [...azureResourceKeys.all, 'accountKeys', accountId] as const,
  deployments: (accountId: string) =>
    [...azureResourceKeys.all, 'deployments', accountId] as const,
  apimInstances: (subscriptionId: string) =>
    [...azureResourceKeys.all, 'apimInstances', subscriptionId] as const,
  apimApis: (apimInstanceId: string) =>
    [...azureResourceKeys.all, 'apimApis', apimInstanceId] as const,
  resourceGroups: (subscriptionId: string) =>
    [...azureResourceKeys.all, 'resourceGroups', subscriptionId] as const,
  projectArmRefs: (tenantId?: string | null) =>
    [...azureResourceKeys.all, 'projectArmRefs', tenantId ?? 'default'] as const,
  /** Prefix key for matching all project ARM ref queries regardless of tenant */
  projectArmRefsPrefix: () => [...azureResourceKeys.all, 'projectArmRefs'] as const,
};

// =============================================================================
// CACHE CONFIGURATION
// =============================================================================

// 8-hour cache, no auto-refetch
const CACHE_CONFIG = {
  staleTime: 1000 * 60 * 60 * 8, // 8 hours
  gcTime: 1000 * 60 * 60 * 8, // 8 hours
  retry: 1,
};

// =============================================================================
// PROJECT PROVISIONING STATE — POLLING HELPERS
// =============================================================================

/**
 * Polling cadence for projects in a non-terminal provisioning state.
 * Short enough to feel responsive after `Create project` returns `Accepted`,
 * slow enough not to hammer the regional control plane.
 */
export const PROJECTS_TRANSITION_POLL_MS = 5000;

/**
 * ARM async-operation TERMINAL states. Everything else is treated as in-flight
 * and triggers polling. We deliberately use a deny-list rather than an
 * allow-list of transitional states so that future or undocumented in-flight
 * states (e.g. a new `Migrating` step) don't silently stop the UI from
 * refreshing.
 *
 * Both `canceled` (ARM standard) and `cancelled` (occasionally seen on older
 * APIs) are included.
 */
const TERMINAL_PROJECT_PROVISIONING_STATES = new Set([
  'succeeded',
  'failed',
  'canceled',
  'cancelled',
]);

/**
 * Returns true when the project is still in flight (any state other than the
 * three terminal ARM states). Missing/null/undefined state is treated as
 * NON-transitional — a malformed response should not trigger an infinite
 * 5-second polling loop. Comparison is case-insensitive.
 */
export const isTransitionalProjectState = (state: string | null | undefined): boolean => {
  if (!state) return false;
  return !TERMINAL_PROJECT_PROVISIONING_STATES.has(state.toLowerCase());
};

const hasAnyTransitionalProject = (projects: AutomationProject[] | undefined): boolean => {
  if (!projects || projects.length === 0) return false;
  return projects.some((p) => isTransitionalProjectState(p.properties?.provisioningState));
};

// =============================================================================
// HOOKS
// =============================================================================


/**
 * Fetch all tenants accessible to the current user
 */
export function useTenants(
  enabled = true
): UseQueryResult<AzureTenant[], Error> {
  const { getArmToken } = useAuth();

  return useQuery({
    queryKey: azureResourceKeys.tenants(),
    queryFn: async () => {
      const token = await getArmToken();
      if (!token) throw new Error('Failed to get access token');
      return getTenants(token);
    },
    enabled,
    ...CACHE_CONFIG,
  });
}

/**
 * Fetch all subscriptions for the current user
 */
export function useSubscriptions(
  enabled = true
): UseQueryResult<AzureSubscription[], Error> {
  const { getArmToken } = useAuth();

  return useQuery({
    queryKey: azureResourceKeys.subscriptions(),
    queryFn: async () => {
      const token = await getArmToken();
      if (!token) throw new Error('Failed to get access token');
      return getSubscriptions(token);
    },
    enabled,
    ...CACHE_CONFIG,
  });
}

/**
 * Fetch all resource groups in a subscription
 */
export function useResourceGroups(
  subscriptionId: string | null,
  enabled = true
): UseQueryResult<AzureResourceGroup[], Error> {
  const { getArmToken } = useAuth();

  return useQuery({
    queryKey: azureResourceKeys.resourceGroups(subscriptionId ?? ''),
    queryFn: async () => {
      const token = await getArmToken();
      if (!token) throw new Error('Failed to get access token');
      return listResourceGroups(token, subscriptionId!);
    },
    enabled: enabled && !!subscriptionId,
    ...CACHE_CONFIG,
  });
}

/**
 * Fetch all automation project locations in a subscription.
 * Uses the Microsoft.Logic ARM API.
 * 
 * @param subscriptionId - The Azure subscription ID
 * @param enabled - Whether the query is enabled
 * @returns QueryResult with array of AzureLocation objects where automation projects can be created
 */
export function useAutomationProjectLocations(
  subscriptionId: string | null,
  enabled = true
): UseQueryResult<AzureLocation[], Error> {
  const { getArmToken } = useAuth();

  return useQuery({
    queryKey: azureResourceKeys.locations(subscriptionId ?? ''),
    queryFn: async () => {
      if (isLocalMode()) {
        return COMMON_AZURE_LOCATIONS;
      }
      const token = await getArmToken();
      if (!token) throw new Error('Failed to get access token');
      
      const response = await fetch(
        `${armEndpoint}/subscriptions/${subscriptionId}/providers/Microsoft.Logic?api-version=2021-04-01`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      
      if (!response.ok) {
        // Fall back to common locations if API fails
        console.warn('[useAutomationProjectLocations] API failed, using fallback locations');
        return COMMON_AZURE_LOCATIONS;
      }
      
      const data = await response.json();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const automationResource = data.resourceTypes.find((resource: any) => resource.resourceType.toLowerCase() === 'automationprojects');
      if (!automationResource) {
        return [];
      }
      
      const locations: AzureLocation[] = (automationResource?.locations ?? [])
        .filter((region: string) => !region.toLowerCase().includes('(stage)'))
        .map((region: string) => {
          // Normalize region name: remove spaces and convert to lowercase (e.g., "East US" -> "eastus")
          const name = region.toLowerCase().replace(/\s+/g, '');
          return {
            id: name,
            name,
            displayName: region,
            regionalDisplayName: region,
            metadata: { regionType: 'Physical', regionCategory: 'Recommended' },
          };
        });
      
      return locations.sort((a, b) => a.displayName.localeCompare(b.displayName));
    },
    enabled: enabled && !!subscriptionId,
    ...CACHE_CONFIG,
  });
}

/**
 * Azure location type
 */
export interface AzureLocation {
  id: string;
  name: string;
  displayName: string;
  regionalDisplayName: string;
  metadata: {
    regionType: string;
    regionCategory: string;
  };
}

/**
 * Common Azure regions as fallback when subscription is not available
 */
const COMMON_AZURE_LOCATIONS: AzureLocation[] = [
  { id: 'brazilus', name: 'brazilus', displayName: 'Brazil South', regionalDisplayName: '(Brazil) Brazil South', metadata: { regionType: 'Physical', regionCategory: 'Recommended' } },
  { id: 'centraluseuap', name: 'centraluseuap', displayName: 'Central US EUAP', regionalDisplayName: '(US) Central US EUAP', metadata: { regionType: 'Physical', regionCategory: 'Recommended' } },
  { id: 'westcentralus', name: 'westcentralus', displayName: 'West Central US', regionalDisplayName: '(US) West Central US', metadata: { regionType: 'Physical', regionCategory: 'Recommended' } },
];

/**
 * Fetch locations supported by Microsoft.Logic provider (tenant-level, no subscription required)
 */
export function useProviderLocations(
  enabled = true
): UseQueryResult<AzureLocation[], Error> {
  const { getArmToken } = useAuth();

  return useQuery({
    queryKey: [...azureResourceKeys.all, 'providerLocations'],
    queryFn: async () => {
      if (isLocalMode()) {
        return COMMON_AZURE_LOCATIONS;
      }
      const token = await getArmToken();
      if (!token) throw new Error('Failed to get access token');
      
      // Use tenant-level provider info to get supported locations for Logic Apps
      const response = await fetch(
        `${armEndpoint}/providers/Microsoft.Logic?api-version=2021-04-01`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      
      if (!response.ok) {
        // Fall back to common locations if provider API fails
        console.warn('[useProviderLocations] Provider API failed, using fallback locations');
        return COMMON_AZURE_LOCATIONS;
      }
      
      const data = await response.json();
      // Extract locations from the provider's resourceTypes (automationProjects or workflows)
      const automationProjectsType = data.resourceTypes?.find(
        (rt: { resourceType: string }) => rt.resourceType === 'automationProjects'
      );
      const workflowsType = data.resourceTypes?.find(
        (rt: { resourceType: string }) => rt.resourceType === 'workflows'
      );
      
      const locationNames: string[] = automationProjectsType?.locations ?? workflowsType?.locations ?? [];
      
      if (locationNames.length === 0) {
        return COMMON_AZURE_LOCATIONS;
      }
      
      // Convert location display names to AzureLocation objects
      // The provider API returns display names like "East US", not internal names like "eastus"
      const locations: AzureLocation[] = locationNames
        .filter(displayName => !(displayName.toLowerCase().includes('(stage)')))
        .map((displayName: string) => {
          // Convert display name to internal name (e.g., "East US" -> "eastus")
          const name = displayName.toLowerCase().replace(/\s+/g, '');
          return {
            id: name,
            name,
            displayName,
            regionalDisplayName: displayName,
            metadata: { regionType: 'Physical', regionCategory: 'Recommended' },
          };
        });

      // Note: "Brazil US" is an Otto control-plane partition label, not an Azure region.
      // Projects deployed there carry `location: "brazilsouth"`. We intentionally do not
      // surface "brazilus" in the region picker — `getProjectLocation()` aliases
      // `brazilsouth` to the `brazilus` CP endpoint internally.
      return locations.sort((a, b) => a.displayName.localeCompare(b.displayName));
    },
    enabled,
    ...CACHE_CONFIG,
  });
}


export function useProjects(enabled = true): UseQueryResult<AutomationProject[], Error> {
  const { getOttoToken, isAuthenticated } = useAuth();
  const { selectedTenantId } = useAppStore();
  const hasExplicitLogicEndpoint = !!(import.meta.env.VITE_LOGIC_ENDPOINT ||
    (typeof window !== 'undefined' && (window as any).__OTTO__LOGIC_ENDPOINT)); // eslint-disable-line @typescript-eslint/no-explicit-any

  return useQuery({
    queryKey: azureResourceKeys.projects(selectedTenantId),
    queryFn: async () => {
      if (isLocalMode() && !hasExplicitLogicEndpoint) {
        // In local mode without explicit endpoint, fetch from mock control plane
        try {
          const endpoint = armEndpoint;
          const response = await fetch(`${endpoint}/subscriptions/local-subscription/providers/Microsoft.Logic/automationProjects`);
          if (response.ok) {
            const data = await response.json();
            return data.value ?? [];
          }
        } catch {
          // Network error — fall through to fallback
        }
        // Fallback if mock control plane is unreachable
        return [{
          id: '/automationProjects/local-resource-group',
          name: 'local-resource-group',
          location: 'localhost',
          resourceGroup: 'local-resource-group',
          subscriptionId: 'local-subscription',
        }];
      }
      const token = await getOttoToken();
      if (!token) throw new Error('Failed to get access token');

      const allProjects: AutomationProject[] = [];
      let nextUrl: string | undefined = `${getLogicEndpoint()}/automationProjects?api-version=${apiVersions.otto}`;

      while (nextUrl) {
        const currentUrl = nextUrl;
        nextUrl = undefined; // Reset before fetch
        
        const response: Response = await fetch(currentUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to fetch automation projects: ${response.status} ${errorText}`);
        }
        const data: { value?: AutomationProject[]; nextLink?: string } = await response.json();
        const pageValues = data.value ?? [];
        allProjects.push(...pageValues);
        
        // Check for next page
        nextUrl = data.nextLink;
      }

      // Return ALL projects, including those in non-terminal provisioning
      // states (Provisioning, Accepted, Creating, Deleting, ...). Hiding them
      // would mean the user can't see a project they just created until ARM
      // settles, which contradicts the dashboard's "Provisioning" status
      // badge. `refetchInterval` below polls the list every 5s while any
      // project is still in flight, so the badge transitions automatically.
      return allProjects;

    },
    enabled: enabled && isAuthenticated,
    ...CACHE_CONFIG,
    // Poll while any project is still provisioning so the user sees the
    // status flip to `Active` / `Failed` without manually refreshing
    // (issue #1645). Pauses while the tab is hidden.
    refetchInterval: (query) =>
      hasAnyTransitionalProject(query.state.data) ? PROJECTS_TRANSITION_POLL_MS : false,
    refetchIntervalInBackground: false,
  });

}

// =============================================================================
// MULTI-REGION PROJECT FETCHING
// =============================================================================

/**
 * Fetch projects from a specific region endpoint (does not mutate global endpoint).
 */
async function fetchProjectsForRegion(
  location: string,
  token: string,
): Promise<AutomationProject[]> {
  // `getProjectLocation` aliases `brazilsouth` -> `brazilus` (Otto's CP partition
  // that physically serves Brazil South projects). For non-Brazil regions it
  // either lowercases or maps `*stage` -> `*(stage)`; `getRegionalEndpoint` then
  // strips the parens, so the two normalizations cancel out and the endpoint
  // URL is unchanged for stage and standard regions.
  const endpoint = getRegionalEndpoint(getProjectLocation(location));
  const allProjects: AutomationProject[] = [];
  let nextUrl: string | undefined = `${endpoint}/automationProjects?api-version=${apiVersions.otto}`;

  while (nextUrl) {
    const currentUrl = nextUrl;
    nextUrl = undefined;

    const response = await fetch(currentUrl, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      // Region may not have any projects or may not support the API — skip silently
      if (response.status === 404 || response.status === 403) return [];
      const errorText = await response.text().catch(() => '');
      throw new Error(`Region ${location}: ${response.status} ${errorText}`);
    }
    const data: { value?: AutomationProject[]; nextLink?: string } = await response.json();
    allProjects.push(...(data.value ?? []));
    nextUrl = data.nextLink;
  }

  return allProjects;
}

/**
 * Result type for multi-region project fetching.
 */
export interface MultiRegionProjectsResult {
  /** All projects loaded so far (grows as regions complete) */
  projects: AutomationProject[];
  /** True only when ALL region queries are still loading */
  isLoading: boolean;
  /** True when at least one region is still loading */
  isFetchingMore: boolean;
  /** Number of regions that have completed (success or error) */
  loadedRegions: number;
  /** Total number of regions being queried */
  totalRegions: number;
  /** Per-region errors (non-fatal — other regions still succeed) */
  errors: Map<string, Error>;
}

/**
 * Fetch projects from ALL supported regions in parallel.
 *
 * Results stream in progressively — projects appear as each region completes,
 * so the user is never blocked waiting for the slowest region.
 * Each region is cached independently by React Query.
 *
 * In local mode, falls back to the standard single-region useProjects().
 */
export function useProjectsMultiRegion(regionFilter?: string | null): MultiRegionProjectsResult {
  const { getOttoToken, isAuthenticated } = useAuth();
  const { selectedTenantId } = useAppStore();
  const locationsQuery = useProviderLocations();
  const locations = locationsQuery.data;
  const hasExplicitEndpoint = !!(
    (typeof import.meta !== 'undefined' && import.meta.env?.VITE_LOGIC_ENDPOINT) ||
    (typeof window !== 'undefined' && (window as any).__OTTO__LOGIC_ENDPOINT) // eslint-disable-line @typescript-eslint/no-explicit-any
  );

  // In local mode or with explicit endpoint, there's only one "region"
  const isLocal = isLocalMode() || hasExplicitEndpoint;

  // Build per-region queries.
  // - When `regionFilter` is set, fetch ONLY that region. We deliberately do
  //   not gate on `useProviderLocations` here: callers like `useCurrentProject`
  //   need to resolve a project as soon as the URL is known, and the locations
  //   API can be slow, fail (e.g. 403), or be unmocked in tests. The region
  //   string from the URL is authoritative — `getRegionalEndpoint` lowercases
  //   it and the regional CP returns 404 if the region isn't valid, which we
  //   already swallow inside `fetchProjectsForRegion`.
  // - When unfiltered, we still fan out across every supported region, which
  //   does require the locations list.
  const regionNames = useMemo(
    () => {
      if (isLocal) return [];
      if (regionFilter) return [regionFilter.toLowerCase()];
      if (!locations) return [];
      return locations.map((l) => l.name);
    },
    [isLocal, locations, regionFilter],
  );

  const regionQueries = useQueries({
    queries: regionNames.map((region) => ({
      queryKey: [...azureResourceKeys.all, 'projects', 'region', selectedTenantId ?? 'default', region] as const,
      queryFn: async () => {
        const token = await getOttoToken();
        if (!token) throw new Error('Failed to get access token');
        return fetchProjectsForRegion(region, token);
      },
      enabled: isAuthenticated && regionNames.length > 0,
      ...CACHE_CONFIG,
      // Poll a region every 5s only while at least one project in that
      // region is non-terminal. Other regions stay quiet — keeps the
      // 30-region fan-out cheap when only one region has a provisioning
      // project (issue #1645).
      refetchInterval: (query: { state: { data: AutomationProject[] | undefined } }) =>
        hasAnyTransitionalProject(query.state.data) ? PROJECTS_TRANSITION_POLL_MS : false,
      refetchIntervalInBackground: false,
    })),
  });

  // Fallback: single-region query for local mode
  const singleRegionQuery = useProjects(isLocal);

  // Merge results progressively
  const result = useMemo<MultiRegionProjectsResult>(() => {
    // Local mode: use single query
    if (isLocal) {
      return {
        projects: singleRegionQuery.data ?? [],
        isLoading: singleRegionQuery.isLoading,
        isFetchingMore: false,
        loadedRegions: singleRegionQuery.isLoading ? 0 : 1,
        totalRegions: 1,
        errors: singleRegionQuery.error
          ? new Map([['local', singleRegionQuery.error]])
          : new Map(),
      };
    }

    // Still waiting for locations — treat as loading.
    // Only relevant when we need the locations list (no explicit `regionFilter`);
    // a filtered query has its region directly from the caller.
    if (!regionFilter && locationsQuery.isLoading) {
      return {
        projects: [],
        isLoading: true,
        isFetchingMore: false,
        loadedRegions: 0,
        totalRegions: 0,
        errors: new Map(),
      };
    }

    // Multi-region: merge as they arrive
    const projects: AutomationProject[] = [];
    const errors = new Map<string, Error>();
    let loaded = 0;

    regionQueries.forEach((q, i) => {
      const regionName = regionNames[i];
      if (q.isSuccess) {
        projects.push(...(q.data ?? []));
        loaded++;
      } else if (q.isError && regionName) {
        errors.set(regionName, q.error as Error);
        loaded++;
      }
    });

    // Sort by region name for stable ordering
    projects.sort((a, b) => (a.location ?? '').localeCompare(b.location ?? ''));

    // isFetchingMore: true when initial load incomplete OR any region is refetching
    const anyFetching = regionQueries.some((q) => q.isFetching || q.isLoading);

    return {
      projects,
      isLoading: loaded === 0 && regionQueries.length > 0,
      isFetchingMore: anyFetching && loaded < regionQueries.length || regionQueries.some((q) => q.isFetching && q.isSuccess),
      loadedRegions: loaded,
      totalRegions: regionQueries.length,
      errors,
    };
  }, [isLocal, singleRegionQuery, regionQueries, regionNames, locationsQuery.isLoading, regionFilter]);

  return result;
}

/**
 * Fetch Cognitive Service accounts (OpenAI, AIServices) in a subscription
 */
export function useCognitiveAccounts(
  subscriptionId: string,
  enabled = true
): UseQueryResult<CognitiveAccount[], Error> {
  const { getArmToken } = useAuth();

  return useQuery({
    queryKey: azureResourceKeys.cognitiveAccounts(subscriptionId),
    queryFn: async () => {
      const token = await getArmToken();
      if (!token) throw new Error('Failed to get access token');
      return getCognitiveAccounts(token, subscriptionId);
    },
    enabled: enabled && !!subscriptionId,
    ...CACHE_CONFIG,
  });
}

/**
 * Fetch AI Foundry projects in a subscription
 */
export function useCognitiveProjects(
  subscriptionId: string,
  enabled = true
): UseQueryResult<CognitiveProject[], Error> {
  const { getArmToken } = useAuth();

  return useQuery({
    queryKey: azureResourceKeys.cognitiveProjects(subscriptionId),
    queryFn: async () => {
      const token = await getArmToken();
      if (!token) throw new Error('Failed to get access token');
      return getCognitiveProjects(token, subscriptionId);
    },
    enabled: enabled && !!subscriptionId,
    ...CACHE_CONFIG,
  });
}

/**
 * Fetch standalone Azure OpenAI accounts (v1 resources) in a subscription
 */
export function useOpenAIAccounts(
  subscriptionId: string,
  enabled = true
): UseQueryResult<CognitiveAccount[], Error> {
  const { getArmToken } = useAuth();

  return useQuery({
    queryKey: azureResourceKeys.openAIAccounts(subscriptionId),
    queryFn: async () => {
      const token = await getArmToken();
      if (!token) throw new Error('Failed to get access token');
      return getOpenAIAccounts(token, subscriptionId);
    },
    enabled: enabled && !!subscriptionId,
    ...CACHE_CONFIG,
  });
}

/**
 * Fetch account details including endpoint
 */
export function useAccountDetails(
  accountId: string,
  enabled = true
): UseQueryResult<AccountDetails, Error> {
  const { getArmToken } = useAuth();

  return useQuery({
    queryKey: azureResourceKeys.accountDetails(accountId),
    queryFn: async () => {
      const token = await getArmToken();
      if (!token) throw new Error('Failed to get access token');
      return getAccountDetails(token, accountId);
    },
    enabled: enabled && !!accountId,
    ...CACHE_CONFIG,
  });
}

/**
 * Fetch account API keys
 */
export function useAccountKeys(
  accountId: string,
  enabled = true
): UseQueryResult<AccountKeys, Error> {
  const { getArmToken } = useAuth();

  return useQuery({
    queryKey: azureResourceKeys.accountKeys(accountId),
    queryFn: async () => {
      const token = await getArmToken();
      if (!token) throw new Error('Failed to get access token');
      return getAccountKeys(token, accountId);
    },
    enabled: enabled && !!accountId,
    ...CACHE_CONFIG,
  });
}

/**
 * Fetch model deployments for an account
 */
export function useDeployments(
  accountId: string,
  enabled = true
): UseQueryResult<ModelDeployment[], Error> {
  const { getArmToken } = useAuth();

  return useQuery({
    queryKey: azureResourceKeys.deployments(accountId),
    queryFn: async () => {
      const token = await getArmToken();
      if (!token) throw new Error('Failed to get access token');
      return getDeployments(token, accountId);
    },
    enabled: enabled && !!accountId,
    ...CACHE_CONFIG,
  });
}

/**
 * Fetch API Management instances in a subscription
 */
export function useApimInstances(
  subscriptionId: string,
  enabled = true
): UseQueryResult<ApimInstance[], Error> {
  const { getArmToken } = useAuth();

  return useQuery({
    queryKey: azureResourceKeys.apimInstances(subscriptionId),
    queryFn: async () => {
      const token = await getArmToken();
      if (!token) throw new Error('Failed to get access token');
      return getApimInstances(token, subscriptionId);
    },
    enabled: enabled && !!subscriptionId,
    ...CACHE_CONFIG,
  });
}

/**
 * Fetch APIs in an API Management instance
 */
export function useApimApis(
  apimInstanceId: string,
  enabled = true
): UseQueryResult<ApimApi[], Error> {
  const { getArmToken } = useAuth();

  return useQuery({
    queryKey: azureResourceKeys.apimApis(apimInstanceId),
    queryFn: async () => {
      const token = await getArmToken();
      if (!token) throw new Error('Failed to get access token');
      return getApimApis(token, apimInstanceId);
    },
    enabled: enabled && !!apimInstanceId,
    ...CACHE_CONFIG,
  });
}

// =============================================================================
// COMPOSITE HOOKS
// =============================================================================

/**
 * Fetch account credentials (endpoint + key) when account is selected
 * Auto-fetches when accountId is provided
 */
export function useAccountCredentials(accountId: string, enabled = true): {
  endpoint: string | undefined;
  key: string | undefined;
  isLoading: boolean;
  error: Error | null;
} {
  const detailsQuery = useAccountDetails(accountId, enabled);
  const keysQuery = useAccountKeys(accountId, enabled);

  return {
    endpoint: detailsQuery.data?.endpoint,
    key: keysQuery.data?.key1,
    isLoading: detailsQuery.isLoading || keysQuery.isLoading,
    error: detailsQuery.error ?? keysQuery.error ?? null,
  };
}

export interface DeploymentOption {
  id: string;
  name: string;
  modelName: string;
  modelFormat: string;
  modelVersion: string;
}

/**
 * Get deployment options with model information for display
 */
export function useDeploymentOptions(accountId: string, enabled = true): {
  deployments: DeploymentOption[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
} {
  const query = useDeployments(accountId, enabled);

  return {
    deployments: (query.data ?? []).map((d) => ({
      id: d.id,
      name: d.name,
      modelName: d.properties.model.name,
      modelFormat: d.properties.model.format,
      modelVersion: d.properties.model.version,
    })),
    isLoading: query.isLoading,
    error: query.error ?? null,
    refetch: query.refetch,
  };
}

// =============================================================================
// AUTOMATION PROJECT ARM IDS (for Azure portal deep links)
// =============================================================================

/**
 * Look up the canonical Azure ARM resource id for each `automationProjects`
 * resource visible to the caller. Otto's data-plane returns short internal ids
 * that the Azure portal can't resolve, so we cross-reference Resource Graph to
 * obtain `/subscriptions/.../resourceGroups/.../automationProjects/<name>`.
 *
 * Returns a `Map` keyed by `<location>/<projectName>` (both lowercased) so
 * lookups remain unambiguous when the same project name exists in multiple
 * regions.
 */
export function useAutomationProjectArmRefs(): UseQueryResult<Map<string, OttoProjectArmRef>, Error> {
  const { getArmToken, isAuthenticated } = useAuth();
  const { selectedTenantId } = useAppStore();

  return useQuery({
    queryKey: azureResourceKeys.projectArmRefs(selectedTenantId),
    queryFn: async () => {
      const token = await getArmToken();
      if (!token) throw new Error('Failed to acquire ARM token');
      const refs = await fetchOttoProjectArmRefs(token);
      const map = new Map<string, OttoProjectArmRef>();
      for (const ref of refs) {
        map.set(armRefKey(ref.location, ref.name), ref);
      }
      return map;
    },
    enabled: isAuthenticated && !isLocalMode(),
    ...CACHE_CONFIG,
  });
}

/** Build the lookup key used by `useAutomationProjectArmRefs`. */
export function armRefKey(location: string, name: string): string {
  return `${(location ?? '').toLowerCase()}/${(name ?? '').toLowerCase()}`;
}
